import type { Module, ActionContext } from 'vuex';
import { magic } from '@gig/magic-sdk';
import type {
    INavigationItem,
    NavigationView,
} from '@gig/magic-sdk/dist/adapter/configs/getNavigation';

import {
    SET_MAIN_MENU_DATA,
    SET_MAIN_MENU_ERROR,
    SET_MAIN_MENU_PENDING,
    SET_MAIN_MENU_OPEN,
    ADD_PREVIOUS_URL,
} from './navigation.constants';
import type {
    INavigationState,
    INormalizedNavItem,
} from './navigation.constants';
import type { IAppState } from '../../constants';

const isIcon = (name: string = '') => name.startsWith('icon-');

export const getters = {
    /**
     * Gets all received menu items from the store
     * unfiltered.
     * @param {INavigationState} state
     * @returns {INavigationItem[]}
     */
    mainMenu(state: INavigationState): INormalizedNavItem[] {
        return state.mainMenu.map((item) => {
            const classNames = item.css.split(' ');
            const finalClasses = classNames.filter((name) => !isIcon(name));
            const iconName = classNames.reduce((currName, name) => {
                return isIcon(name) ? name.replace('icon-', '') : currName;
            }, 'games');
            const submenuDesktop = item.css.includes('submenu-desktop');
            const submenuMobile = item.css.includes('submenu-mobile');

            return {
                ...item,
                icon: iconName,
                submenuDesktop,
                submenuMobile,
                css: finalClasses.map((name) => `item--${name}`).join(' '),
            };
        });
    },

    /**
     * Gets the error from the back end if the main menu
     * could not be loaded.
     * @param {INavigationState} state
     * @returns {string}
     */
    mainMenuError(state: INavigationState) {
        return state.mainMenuError;
    },

    /**
     * Returns whether or not the main menu is currently
     * loading from the backend.
     * @param {INavigationState} state
     * @returns {boolean}
     */
    isMainMenuLoading(state: INavigationState) {
        return state.mainMenuLoading;
    },

    /**
     * Whether or not the main menu is currently being shown
     * to the user.
     * @param {INavigationState} state
     * @returns {boolean}
     */
    isMainMenuOpen(state: INavigationState) {
        return state.isMainMenuOpen;
    },

    /**
     * The applications router previous URL or
     * '/' if none exists.
     * @param {INavigationState} state
     * @returns {string}
     */
    previousUrl(state: INavigationState): string {
        return state.previousUrls[state.previousUrls.length - 1] || '/';
    },
};

export const actions = {
    /**
     * Fetches the navigation menu from the magic backend.
     * @param {ActionContext<INavigationState, IAppState>} { commit }
     * @param {{ navigationView: NavigationView, language: string }} { navigationView, language }
     * @returns
     */
    async fetchMainMenu(
        { commit }: ActionContext<INavigationState, IAppState>,
        {
            navigationView,
            language,
        }: { navigationView: NavigationView; language: string },
    ) {
        commit(SET_MAIN_MENU_PENDING);

        try {
            const navItems = await magic.methods.configs.getNavigation(
                navigationView,
                language,
            );
            commit(SET_MAIN_MENU_DATA, navItems);
        } catch (error) {
            // TODO: Proper error handling.
            commit(SET_MAIN_MENU_ERROR, error.toString());
        }
    },

    /**
     * Toggles the hide/show flag of the main menu.
     * @param {ActionContext<INavigationState, IAppState>} { commit, state }
     */
    toggleMainMenu({
        commit,
        state,
    }: ActionContext<INavigationState, IAppState>) {
        commit(SET_MAIN_MENU_OPEN, !state.isMainMenuOpen);
    },

    /**
     * Sets the flag to false for the main menu.
     */
    closeMainMenu({
        commit,
    }: ActionContext<INavigationState, IAppState>) {
        commit(SET_MAIN_MENU_OPEN, false);

    },

    /**
     * Adds the previous URL to the routing stack.
     * @param {ActionContext<INavigationState, IAppState>} { commit }
     * @param {string} url
     */
    setPreviousUrl(
        {
            commit,
            state: { previousUrls },
        }: ActionContext<INavigationState, IAppState>,
        url: string,
    ) {
        // Do not add URLs that have been duplicated (eg. when navigating backwards).
        if (previousUrls[previousUrls.length - 1] === url) {
            return;
        }

        commit(ADD_PREVIOUS_URL, url);
    },
};

export const navigation: Module<INavigationState, IAppState> = {
    namespaced: true,
    state: {
        mainMenu: [],
        mainMenuLoading: true,
        mainMenuError: null,
        isMainMenuOpen: false,
        previousUrls: [],
    },
    getters,
    actions,
    mutations: {
        [SET_MAIN_MENU_DATA](state, payload: INavigationItem[]) {
            state.mainMenuLoading = false;
            state.mainMenu = payload;
        },
        [SET_MAIN_MENU_ERROR](state, payload: string) {
            state.mainMenuLoading = false;
            state.mainMenu = [];
            state.mainMenuError = payload;
        },
        [SET_MAIN_MENU_PENDING](state) {
            state.mainMenuLoading = true;
            state.mainMenuError = null;
        },
        [SET_MAIN_MENU_OPEN](state, payload: boolean) {
            state.isMainMenuOpen = payload;
        },
        [ADD_PREVIOUS_URL](state, payload: string) {
            if (state.previousUrls.length >= 15) {
                state.previousUrls.splice(14, state.previousUrls.length - 14);
            }

            state.previousUrls.push(payload);
        },
    },
};
